/**
 * PlayStrip + ReasonChip — the scoped projection of the one Play engine
 * (Interaction Specs §A1; Design System §2.7). Play is a *layer*, not a page:
 * the same ranked queue renders globally (the driver), per-mode (the Queue
 * view), and per-object (the Recommendations section). PlayStrip is that
 * scoped, quiet strip — top N ranked commitment-actions, each stating its
 * reason (§A3, "no card is unexplained").
 *
 * Three responses, always the same (§A5): Do · Defer · Dismiss(+reason), plus
 * the cross-cutting Delegate (CP-8). Pure presentation — it ranks nothing and
 * fetches nothing; the parent supplies the already-ranked `actions` (from
 * `play/rank.ts`) and owns the signal handlers.
 */

import * as React from "react";
import type { PlayActionDTO, PlaySignal } from "../../api/contracts/play";
import { KIND_META, cleanReasons } from "./shared";
import { Button } from "../../button";
import { DropdownMenu } from "../../dropdown-menu";
import { MoreHorizontal } from "../../icons";
import type { ContextMenuItem } from "../../context-menu";
import { cn } from "../../utils";

/** The Play "why" — a quiet one-line reason chip (Design System §2.7). */
export function ReasonChip({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span className={cn("type-subtext text-muted-foreground", className)}>
      why: {children}
    </span>
  );
}

export interface PlayStripProps {
  /** Already-ranked actions (highest-value first). */
  actions: PlayActionDTO[];
  /** Total available (when more than `limit` are ranked). */
  total?: number;
  /** Cap rendered rows; default 3 (the "top 1–3" strip). */
  limit?: number;
  /** Strip title; omit inside an object page's Recommendations section. */
  title?: React.ReactNode;
  /** Open / act on the commitment (parent navigates or runs the action). */
  onDo: (action: PlayActionDTO) => void;
  onDefer?: (action: PlayActionDTO) => void;
  onDismiss?: (action: PlayActionDTO, reason?: string) => void;
  /** Hand the commitment to an agent (CP-8). */
  onDelegate?: (action: PlayActionDTO) => void;
  /** Emit a feedback signal (shown/started/…); optional. */
  onSignal?: (action: PlayActionDTO, signal: PlaySignal) => void;
  doLabel?: string;
  emptyMessage?: React.ReactNode;
  className?: string;
}

export function PlayStrip({
  actions,
  total,
  limit = 3,
  title,
  onDo,
  onDefer,
  onDismiss,
  onDelegate,
  doLabel = "Do",
  emptyMessage = "Nothing needs you right now.",
  className,
}: PlayStripProps) {
  // Optimistic removal: a Do / Defer / Dismiss / Delegate acts immediately —
  // the row leaves the strip and the next-ranked action slides up — instead of
  // waiting on the feedback POST + re-fetch (which often returns the same action,
  // so the card appeared to do nothing). The parent still posts the signal.
  const [acted, setActed] = React.useState<Set<string>>(new Set());
  const act = (action: PlayActionDTO, handler?: (a: PlayActionDTO) => void) => {
    setActed((prev) => {
      const next = new Set(prev);
      next.add(action.key);
      return next;
    });
    handler?.(action);
  };
  const visible = actions.filter((a) => !acted.has(a.key));
  const shown = visible.slice(0, limit);
  const remaining = Math.max(0, (total ?? actions.length) - acted.size - shown.length);

  return (
    <div className={cn("flex flex-col", className)}>
      {title ? (
        <div className="mb-1 flex items-center gap-1.5 type-body font-medium">
          <span aria-hidden>▶</span>
          {title}
        </div>
      ) : null}
      {shown.length === 0 ? (
        <div className="py-2 type-body text-muted-foreground">{emptyMessage}</div>
      ) : (
        <ul className="flex flex-col">
          {shown.map((action) => {
            const meta = KIND_META[action.kind];
            const reason = cleanReasons(action)[0];
            const KindIcon = meta.Icon;
            // The secondary responses (Delegate · Defer · Dismiss) collapse into
            // one "…" overflow so each row carries a single primary affordance
            // (Do) instead of four competing buttons repeated down the list.
            const menuItems: ContextMenuItem[] = [];
            if (onDelegate)
              menuItems.push({ label: "Delegate", onSelect: () => act(action, onDelegate) });
            if (onDefer)
              menuItems.push({ label: "Defer", onSelect: () => act(action, onDefer) });
            if (onDismiss)
              menuItems.push({
                label: "Dismiss",
                destructive: true,
                onSelect: () => act(action, (a) => onDismiss(a)),
              });
            return (
              <li
                key={action.key}
                className="flex items-center gap-3 border-b border-border/40 py-2 first:pt-0 last:border-b-0 last:pb-0"
              >
                <KindIcon className="size-4 shrink-0 text-muted-foreground" />
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate text-foreground">{action.title}</span>
                  <span className="flex items-center gap-2">
                    {reason ? <ReasonChip>{reason}</ReasonChip> : null}
                    {action.payoff ? (
                      <span className="type-subtext text-muted-foreground">
                        {action.payoff}
                      </span>
                    ) : null}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button size="sm" onClick={() => act(action, onDo)}>
                    {doLabel}
                  </Button>
                  {menuItems.length > 0 ? (
                    <DropdownMenu items={menuItems} align="end">
                      <Button variant="ghost" size="icon" aria-label="More actions">
                        <MoreHorizontal className="h-3.5 w-3.5" strokeWidth={1.75} />
                      </Button>
                    </DropdownMenu>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {remaining > 0 ? (
        <div className="pt-2 type-subtext text-muted-foreground">
          +{remaining} more in the Queue
        </div>
      ) : null}
    </div>
  );
}
